import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Building2, ChevronRight, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../api/client";
import type { CompanyDetail } from "@mvk/shared";
import { useAppStore } from "../store/useAppStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { SkeletonList } from "../components/SkeletonList";
import { formatCompanyLocation } from "../utils/locationLabel";
import { companyDetailToUiCompany } from "../api/scan";

type BoothPayload = {
  booth: string;
  hall?: string;
  companies: CompanyDetail[];
};

export function BoothCompaniesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId: routeEventId, booth: boothParam } = useParams<{
    eventId: string;
    booth: string;
  }>();
  const { selectedEventId, selectEvent } = useAppStore();
  const eventId = routeEventId || selectedEventId || "";
  const boothKey = decodeURIComponent(boothParam ?? "");

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<BoothPayload | null>(null);

  useEffect(() => {
    if (!eventId || !boothKey) return;
    if (routeEventId && routeEventId !== selectedEventId) {
      selectEvent(routeEventId);
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiRequest<BoothPayload>(
          `/events/${eventId}/shared-booths/${encodeURIComponent(boothKey)}`,
          { audience: "visitor" },
        );
        if (!cancelled) setPayload(data);
      } catch {
        if (!cancelled) setPayload(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, boothKey, routeEventId, selectedEventId, selectEvent]);

  return (
    <>
      <PageHeader
        title={
          payload?.booth
            ? t("location.booth", { value: payload.booth })
            : t("booth_slot.title")
        }
        subtitle={
          payload
            ? t("booth_slot.subtitle", { n: payload.companies.length })
            : undefined
        }
        back
      />
      <PageShell>
        {loading ? (
          <SkeletonList count={6} variant="row" />
        ) : !payload || payload.companies.length === 0 ? (
          <div className="pt-16 text-center text-gray-500 text-[14px]">
            {t("booth_slot.empty")}
          </div>
        ) : (
          <div className="space-y-2">
            {payload.hall ? (
              <p className="text-[12px] text-gray-500 flex items-center gap-1 mb-2">
                <MapPin size={12} />
                {payload.hall}
              </p>
            ) : null}
            {payload.companies.map((c) => {
              const ui = companyDetailToUiCompany(c);
              return (
                <GlassCard
                  key={c.id}
                  variant="default"
                  padding="sm"
                  className="cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => navigate(`/companies/${c.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <CompanyAvatar
                      name={ui.name}
                      logoUrl={ui.logoUrl}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] text-gray-900 truncate">{ui.name}</p>
                      <p className="text-[12px] text-gray-500 truncate">
                        {ui.category ||
                          formatCompanyLocation({
                            booth: ui.booth,
                            hall: ui.hall,
                            hideHall: true,
                          })}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </div>
                </GlassCard>
              );
            })}
            {payload.companies.length === 0 ? (
              <div className="flex items-center gap-2 text-gray-400 text-[13px]">
                <Building2 size={16} />
                {t("booth_slot.empty")}
              </div>
            ) : null}
          </div>
        )}
      </PageShell>
    </>
  );
}
